import { Injectable, inject } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { map, shareReplay, catchError, finalize } from 'rxjs/operators';
import { BaseHttpService } from './base-http.service';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

type CacheMap = Map<string, CacheEntry<unknown>>;
type InFlightMap = Map<string, Observable<unknown>>;

const DEFAULT_CACHE_TTL_MS = 5 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class ApiCacheService {
  private readonly baseHttp = inject(BaseHttpService);

  private cache: CacheMap = new Map();
  private inFlight: InFlightMap = new Map();

  /**
   * Generic method to fetch and cache API responses
   * Delegates HTTP handling and error processing to BaseHttpService
   * Use this for APIs that need caching
   * @param url The API endpoint URL
   * @param payload Optional request payload
   * @param cacheKey Optional custom cache key. If not provided, url + serialized payload is used
   * @param isSuccess Optional function to determine if response should be cached (applied to the extracted data)
   */
  get<T>(
    url: string,
    payload?: Record<string, string | number | boolean>,
    cacheKey?: string,
    isSuccess: (response: T) => boolean = () => true,
  ): Observable<T> {
    // Generate cache key from URL and payload if not provided
    const key = cacheKey || this.generateCacheKey(url, payload);

    // Check cache first
    const cached = this.cache.get(key) as CacheEntry<T> | undefined;
    if (cached && Date.now() - cached.timestamp < DEFAULT_CACHE_TTL_MS) {
      return of(cached.data);
    }
    if (cached) this.cache.delete(key);

    // Check for in-flight requests
    const pending = this.inFlight.get(key) as Observable<T> | undefined;
    if (pending) {
      return pending;
    }

    // Make the API call through BaseHttpService which handles all error processing
    // BaseHttpService.sendRequest returns the extracted data (T) and handles API/HTTP errors
    const req$ = this.baseHttp.sendRequest<T>(url, payload).pipe(
      map((response) => {
        // Cache only if the parent service says the extracted data is valid
        if (isSuccess(response)) {
          const entry: CacheEntry<T> = {
            data: response,
            timestamp: Date.now(),
          };
          this.cache.set(key, entry as CacheEntry<unknown>);
        }
        return response;
      }),
      shareReplay(1),
      catchError((err) => {
        this.inFlight.delete(key);
        return throwError(() => err);
      }),
      finalize(() => {
        this.inFlight.delete(key);
      }),
    );

    this.inFlight.set(key, req$);
    return req$;
  }

  /**
   * Clear entire cache or specific entries
   * @param key Optional specific cache key to clear
   */
  clear(key?: string) {
    if (key) {
      this.cache.delete(key);
      this.inFlight.delete(key);
    } else {
      this.cache.clear();
      this.inFlight.clear();
    }
  }

  /** Clears all cached and in-flight entries whose key starts with `prefix`. */
  clearByPrefix(prefix: string): void {
    for (const key of this.cache.keys()) {
      if (key.startsWith(prefix)) this.cache.delete(key);
    }
    for (const key of this.inFlight.keys()) {
      if (key.startsWith(prefix)) this.inFlight.delete(key);
    }
  }

  /**
   * Generate a cache key from URL and payload
   */
  private generateCacheKey(
    url: string,
    payload?: Record<string, string | number | boolean>,
  ): string {
    if (!payload) return url;
    return `${url}:${JSON.stringify(payload)}`;
  }
}
